import { useEffect, useRef } from 'react';
import { useInstanceId } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';
import { bridge, isWeb, sanitizeRedirectPath } from 'shared/src/utils';

/**
 * 이미 로그인된 사용자가 로그인 화면(Login/EmailLogin)에 "진입"하면 되돌려보낸다.
 * - 웹: ?redirect(없으면 홈)로 replace (뒤로가기로 로그인에 다시 안 돌아오게).
 * - 네이티브: 로그인 인스턴스를 닫는다.
 *
 * myInfo는 비동기이므로 최초 로드된 상태(진입 시점)만 기준으로 판단한다. 진입 시
 * anonymous였다면 이후 소셜/이메일 로그인으로 로그인되더라도 게이트를 발동하지 않는다 —
 * 그 복귀는 각 로그인 성공 로직이 담당하며, 여기서 끼어들면 신규가입 완료 드로어 등이 끊긴다.
 */
export const useRedirectIfLoggedIn = () => {
  const { data: myInfo } = useMyInfo();
  const instanceId = useInstanceId();
  const handledRef = useRef(false);
  const entryAnonymousRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (handledRef.current || myInfo == null) return;
    // 진입 시점(최초 로드) 상태를 래치한다.
    if (entryAnonymousRef.current == null) {
      entryAnonymousRef.current = myInfo.is_anonymous;
    }
    // 진입 시 이미 로그인된 경우에만 되돌려보낸다.
    if (entryAnonymousRef.current) return;
    handledRef.current = true;

    if (isWeb) {
      const redirect = new URLSearchParams(window.location.search).get(
        'redirect',
      );
      // 외부 조작 가능한 값이므로 내부 경로만 허용(오픈 리다이렉트 방지).
      window.location.replace(sanitizeRedirectPath(redirect));
      return;
    }
    bridge.closeInstance({ instanceId });
  }, [myInfo, instanceId]);
};
